'use client';
import React, { useMemo, useState, FormEvent, ChangeEvent } from 'react';

type Dept = 'recruiting' | 'sourcing' | 'ops' | 'account' | 'marketing';
type Loc = 'remote' | 'moscow' | 'spb';
type Kind = 'full' | 'part' | 'intern';

type Job = {
  id: number; title: string; dept: Dept; loc: Loc; type: Kind;
  level: string; salary: string; tags: string[]; desc: string;
};

const JOBS: Job[] = [
  { id: 1, title: 'Рекрутер (RPO)', dept: 'recruiting', loc: 'remote', type: 'full', level: 'Middle', salary: 'от 120 000 ₽ + бонусы', tags: ['ATS', 'SLA', 'интервью'], desc: 'Ведение воронки, взаимодействие с HM, отчётность по SLA.' },
  { id: 2, title: 'Сорсер', dept: 'sourcing', loc: 'remote', type: 'full', level: 'Junior/Middle', salary: 'от 90 000 ₽ + бонусы', tags: ['Boolean', 'HH'], desc: 'Поиск и первичный контакт с кандидатами, ведение CRM.' },
  { id: 3, title: 'Account / PM', dept: 'account', loc: 'moscow', type: 'full', level: 'Middle/Senior', salary: 'от 150 000 ₽ + KPI', tags: ['SLA', 'коммуникации', 'аналитика'], desc: 'Ведение портфеля клиентов, отчётность, управление SLA.' },
  { id: 4, title: 'Операционный менеджер', dept: 'ops', loc: 'spb', type: 'full', level: 'Middle', salary: 'от 130 000 ₽', tags: ['KPI', 'процессы', 'BI'], desc: 'Постановка процессов, метрики, координация команд.' },
  { id: 5, title: 'Маркетолог (контент)', dept: 'marketing', loc: 'remote', type: 'part', level: 'Middle', salary: 'почасовая + бонус', tags: ['контент', 'вебинары', 'лендинги'], desc: 'Контент-план, лендинги, вебинары и кейсы.' },
  { id: 6, title: 'Стажёр рекрутера', dept: 'recruiting', loc: 'remote', type: 'intern', level: 'Intern', salary: 'стипендия', tags: ['обучение', 'ресёрч'], desc: 'Сорсинг, расписание интервью, админ-поддержка.' },
];

const kindLabel = (t: Kind) => t === 'full' ? 'Полная занятость' : t === 'part' ? 'Частичная' : 'Стажировка';
const labelLoc = (l: Loc) => l === 'remote' ? 'Remote' : l === 'moscow' ? 'Грозный' : 'Гудермес';

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <article className={`rounded-2xl p-6 ${className || ''}`} style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>{children}</article>
);

const Chip: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'rgba(255,255,255,.14)' }}>{children}</a>
);

export default function CareersPage() {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState<'' | Dept>('');
  const [loc, setLoc] = useState<'' | Loc>('');
  const [kind, setKind] = useState<'' | Kind>('');

  const [jobForm, setJobForm] = useState({
    name: '',
    email: '',
    phone: '',
    desiredRole: '',
    experienceSummary: '',
    formName: 'Карьера в INTERIM',
  });

  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [showConsentWarning, setShowConsentWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleJobChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setJobForm({ ...jobForm, [name]: value });
  };

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked) {
      setShowConsentWarning(false);
    }
  };

  const handleJobSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setShowConsentWarning(false);

    if (!isConsentChecked) {
      setIsSubmitting(false);
      setShowConsentWarning(true);
      return;
    }
    try {

      if (!jobForm.name || !jobForm.email) {
        setSubmitStatus('error');
        return;
      }

      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/job-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...jobForm,
            formName: 'Карьера в INTERIM', 
          }
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setJobForm({
          name: '',
          email: '',
          phone: '',
          desiredRole: '',
          experienceSummary: '',
          formName: 'Карьера в INTERIM',
        });
        setIsConsentChecked(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };


  const items = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return JOBS.filter(j => {
      const s = (j.title + ' ' + j.tags.join(' ') + ' ' + j.desc).toLowerCase();
      const okQ = !ql || s.includes(ql);
      const okD = !dept || j.dept === dept;
      const okL = !loc || j.loc === loc;
      const okT = !kind || j.type === kind;
      return okQ && okD && okL && okT;
    });
  }, [q, dept, loc, kind]);

  const customStyles = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
        
        .custom-checkbox {
            appearance: none;
            cursor: pointer;
            transition: all 0.15s ease-in-out;
            border-color: #9ca3af; 
        }
        
        .custom-checkbox:checked {
            background-color: #FFC300; 
            border-color: #FFC300;     
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23102a43' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
            background-size: 80%; 
            background-repeat: no-repeat;
            background-position: center;
        }
    `;

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen font-sans">
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <Chip href="#openings">Вакансии</Chip>
            <Chip href="#benefits">Преимущества</Chip>
            <Chip href="#process">Как мы нанимаем</Chip>
            <Chip href="#culture">Культура</Chip>
            <Chip href="#faq">FAQ</Chip>
            <Chip href="#apply">Отклик</Chip>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Карьера в INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Растём быстро и умно: прозрачные KPI, поддержка наставников, гибкий график и реальные бонусы за результат.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#openings" className="px-[15px] py-[11px] rounded-xl font-semibold border border-blue-600 text-blue-600 bg-white">
              Смотреть вакансии
            </a>
            <a href="#apply" className="px-4 py-3 rounded-xl font-semibold" style={{ background: '#FFC300', color: '#102a43' }}>Откликнуться</a>
          </div>
        </div>
      </header>
      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="openings">
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <h2 className="text-3xl md:text-4xl font-bold">Открытые вакансии</h2>
            <span className="text-sm text-gray-300">Найдено: {items.length}</span>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-5">
            <input value={q} onChange={e => setQ(e.target.value)} className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" placeholder="Поиск по названию/ключевым словам…" />
            <select value={dept} onChange={e => setDept(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
              <option value="">Отдел: все</option>
              <option value="recruiting">Рекрутинг</option>
              <option value="sourcing">Сорсинг</option>
              <option value="ops">Операции</option>
              <option value="account">Аккаунт/PM</option>
              <option value="marketing">Маркетинг</option>
            </select>
            <select value={loc} onChange={e => setLoc(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
              <option value="">Локация: все</option>
              <option value="remote">Удалёнка</option>
              <option value="moscow">Москва</option>
              <option value="spb">Санкт-Петербург</option>
            </select>
            <select value={kind} onChange={e => setKind(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
              <option value="">Тип: любой</option>
              <option value="full">Полная занятость</option>
              <option value="part">Частичная</option>
              <option value="intern">Стажировка</option>
            </select>
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map(j => (
              <Card key={j.id}>
                <div className="text-sm text-emerald-300 mb-1">{`${kindLabel(j.type)} · ${labelLoc(j.loc)}`}</div>
                <h3 className="text-xl font-semibold">{j.title}</h3>
                <p className="text-sm text-gray-200 mt-1">{j.level} · {j.salary}</p>
                <p className="text-sm text-gray-200 mt-3">{j.desc}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {j.tags.map(t => <span key={t} className="px-2.5 py-1 rounded-full text-xs" style={{ background: 'rgba(255,255,255,.12)' }}>{t}</span>)}
                </div>
                <a href="#apply" className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold" style={{ background: 'rgba(255,255,255,.1)' }}>Откликнуться</a>
              </Card>
            ))}
          </div>
          {items.length === 0 && <p className="text-gray-300">Нет совпадений. Измените фильтры или запрос.</p>}
        </section>

        <section id="benefits" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Почему у нас классно</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['Гибкость и удаленка', 'Гибкий график, удалёнка/гибрид, современный стек инструментов.'],
              ['Сильная команда', 'Наставники, внутренние тренинги, карьерные планы и ревью.'],
              ['Прозрачные бонусы', 'Бонусная сетка за закрытия и качество, KPI понятны заранее.'],
            ].map(([t, d]) => (
              <Card key={t}><h3 className="text-xl font-semibold mb-2">{t}</h3><p className="text-sm text-gray-200">{d}</p></Card>
            ))}
          </div>
        </section>
        <section id="process" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Как проходит найм</h2>
          <ol className="grid gap-6 md:grid-cols-3">
            {[
              ['1. Отклик', 'Заполните форму и прикрепите резюме/портфолио.'],
              ['2. Скрининг', 'Короткий звонок HR: ожидания, опыт, условия.'],
              ['3. Интервью', 'Интервью с лидом и практический кейс.'],
              ['4. Финал', 'Встреча с руководителем, оффер и план онбординга.'],
              ['5. Онбординг', 'Наставник, цели 30/60/90, доступы и обучение.'],
            ].map(([t, d]) => (
              <Card key={t}><div className="text-2xl font-bold mb-1">{t}</div><p className="text-sm text-gray-200">{d}</p></Card>
            ))}
          </ol>
        </section>
        <section id="culture" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Культура и ценности</h2>
          <ul className="grid gap-3 md:grid-cols-2 text-sm text-gray-200">
            {[
              'Прозрачные метрики и честная обратная связь',
              'Ответственность за результат и поддержка команды',
              'Постоянное обучение, обмен опытом и внутренние митапы',
              'Забота о балансе: отпуск, sick days, гибкий график',
            ].map(i => (
              <li key={i} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>• {i}</li>
            ))}
          </ul>
        </section>
        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Частые вопросы</h2>
          <div className="space-y-3">
            {[
              ['Какой график?', 'Гибкий. Есть core-время для созвонов, остальное — по договорённости с командой.'],
              ['Удалёнка?', 'Да, возможны remote и гибрид. Офисы — Грозный (коворкинги).'],
              ['Есть наставники?', 'Да, у каждого новичка есть наставник и план 30/60/90.'],
            ].map(([q, a]) => (
              <details key={q} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                <summary className="cursor-pointer font-semibold">{q}</summary>
                <p className="mt-2 text-sm text-gray-200">{a}</p>
              </details>
            ))}
          </div>
        </section>
        <section id="apply" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Отклик на вакансию</h2>
          <form
            onSubmit={handleJobSubmit}
            className="rounded-2xl p-6 grid gap-4 md:grid-cols-2"
            style={{ background: 'rgba(255,255,255,.08)' }}
          >
            <input
              name="name"
              value={jobForm.name}
              onChange={handleJobChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Имя*"
              required
            />
            <input
              name="email"
              value={jobForm.email}
              onChange={handleJobChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="E-mail*"
              type="email"
              required
            />
            <input
              name="phone"
              value={jobForm.phone}
              onChange={handleJobChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Телефон"
            />
            <input
              name="desiredRole"
              value={jobForm.desiredRole}
              onChange={handleJobChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Желаемая роль (или ссылка на вакансию)"
            />
            <textarea
              name="experienceSummary"
              value={jobForm.experienceSummary}
              onChange={handleJobChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Кратко о вашем опыте/результатах"
            ></textarea>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={isConsentChecked}
                  onChange={handleConsentChange}
                  className="w-5 h-5 rounded border-2 custom-checkbox appearance-none"
                />
                <span className="text-sm text-gray-200">
                  Я соглашаюсь с <a href="#" className="underline hover:text-white">обработкой персональных данных</a>*
                </span>
              </label>
            </div>

            {showConsentWarning && (
              <div className="md:col-span-2 p-3 rounded-xl text-center font-medium  border-white text-white transition-all duration-300">
                Пожалуйста, дайте согласие на обработку персональных данных, чтобы отправить отклик.
                <button
                  onClick={() => setShowConsentWarning(false)}
                  className="ml-3 font-semibold text-yellow-100 hover:text-white underline"
                >
                  Скрыть
                </button>
              </div>
            )}

            <button
              type="submit"
              className="rounded-xl px-4 py-3 font-semibold md:col-span-2 transition duration-300 transform hover:scale-[1.01]"
              style={{ background: '#FFC300', color: '#102a43' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>
            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                ✅ Ваш отклик успешно отправлен!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                ❌ Ошибка при отправке. Пожалуйста, проверьте, что все поля заполнены, и попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}
